import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { AGENT_URL } from '@/config/env'
import type { RubricType } from '@/hooks/useConfigAdmin'
import { fetchJsonWithAuth, requireAuthToken } from '@/lib/api'
import { callRubricKeys } from '@/lib/queryKeys'

/** One rubric the org's calls are graded against, as the Call Logs filters need it. */
export interface CallRubricOption {
  /**
   * Picks the rubric's results whatever title they were graded under: more
   * than one for rubrics saved under one title before titles were unique,
   * none for a rubric saved without an id (matched by title).
   */
  rubric_ids?: string[]
  title: string
  type: RubricType
  /** Labels (single- and multi-label) or level names, lowest first (rating). */
  options: string[]
}

interface CallRubricOptionsResponse {
  data: CallRubricOption[]
}

/**
 * The org's rubrics for the Call Logs rubric filters (Careforce's when the org
 * has none, as the judge grades with). Empty for viewers who may not read
 * rubric results: the server applies the same gate as the rubric report.
 */
export function useCallRubricOptions() {
  const { getToken } = useAuth()
  const { organization, isLoaded } = useOrganization()

  const query = useQuery({
    queryKey: callRubricKeys.all(organization?.id),
    enabled: !!organization?.id,
    queryFn: async ({ signal }) => {
      const token = await requireAuthToken(getToken)
      const { data } = await fetchJsonWithAuth<CallRubricOptionsResponse>({
        token,
        url: `${AGENT_URL}/data/calls/rubric-options`,
        signal,
        errorMessage: 'Failed to fetch rubric options',
      })
      return data
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    // A link with rubric filters waits on this, so a failure gives up quickly
    // and the page renders without the rubric filters.
    retry: 1,
  })

  return {
    data: query.data,
    // Still loading until Clerk has the org: the query is disabled before
    // then, which would otherwise read as settled with no rubrics.
    isLoading: !isLoaded || query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}
